import { useState, useContext, useEffect } from "react";

import { useHistory, useLocation } from "react-router-dom";

import { makeStyles } from "@mui/styles";

import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import IconButton from "@mui/material/IconButton";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import TheatersOutlinedIcon from "@mui/icons-material/TheatersOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DesktopMacOutlinedIcon from "@mui/icons-material/DesktopMacOutlined";

// My Files
import { DatabaseContext, UserContext } from "../../context";
import UserAvatar from "../management/UserAvatar";
import { GLOBAL_ROLE } from "../admin/fileVisibility";
import useHasCurrentDesktopLicence from "../desktopapp/useCurrentDesktopLicence";
import { countUnreadMessages } from "../messages/messageReadReceipts";
import {
    BRAND_GRADIENT,
    BRAND_GREEN,
    BRAND_HAIRLINE,
} from "./brandSurface";

import {
    Box,
    ButtonBase,
    Divider,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Portal,
    Typography,
} from "@mui/material";

export const DESKTOP_NAV_WIDTH = 240;

// The header carries the brand colour across the top of the drawer so that, on
// the pinned layout, it and the AppBar read as one green band.
//
// The AppBar takes its height from a 64px toolbar plus its own 1px bottom
// border. Everything here is border-box, where the border counts against the
// declared height instead, so the header needs the combined 65 for the two
// hairlines to land on the same baseline.
const NAV_HEADER_HEIGHT = 65;

// Nav rows live in `sx` rather than makeStyles: App.jsx wraps the tree in both
// StylesProvider and StyledEngineProvider with injectFirst, so JSS-vs-emotion
// ordering is ambiguous and JSS overrides of MUI component defaults are not
// reliably applied.
// Typography paints its variant styles through emotion, so these have to be sx
// too: a makeStyles class of equal specificity loses the font, size and
// spacing back to the body1 defaults.
const brandNameSx = {
    fontFamily: "DraigRegular",
    fontSize: 20,
    lineHeight: 1,
    letterSpacing: "0.02em",
};

const sectionLabelSx = {
    mt: 1.5,
    mx: 1.25,
    mb: 0.5,
    color: "text.disabled",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    lineHeight: 1.4,
};

const navItemSx = (theme) => ({
    minHeight: 40,
    mb: "2px",
    px: 1.25,
    py: 0,
    borderRadius: 2,
    color: "text.secondary",
    transition: "background-color 120ms ease, color 120ms ease",
    "& .MuiListItemIcon-root": {
        minWidth: 32,
        color: "text.secondary",
        transition: "color 120ms ease",
    },
    "& .MuiListItemIcon-root > svg": {
        fontSize: 21,
    },
    "& .MuiListItemText-primary": {
        fontSize: 14,
        fontWeight: 500,
    },
    "&:hover": {
        backgroundColor: "action.hover",
    },
    "&.Mui-selected": {
        color: "primary.main",
        backgroundColor:
            theme.palette.mode === "dark"
                ? "rgba(31, 184, 94, 0.14)"
                : "rgba(0, 149, 61, 0.1)",
    },
    "&.Mui-selected:hover": {
        backgroundColor:
            theme.palette.mode === "dark"
                ? "rgba(31, 184, 94, 0.2)"
                : "rgba(0, 149, 61, 0.14)",
    },
    "&.Mui-selected .MuiListItemIcon-root": {
        color: "primary.main",
    },
    "&.Mui-selected .MuiListItemText-primary": {
        fontWeight: 600,
    },
    "&:focus-visible": {
        outline: "2px solid",
        outlineColor: "primary.main",
        outlineOffset: -2,
    },
});

const useStyles = makeStyles((theme) => ({
    "@keyframes drawerContentEnter": {
        "0%": {
            opacity: 0,
            transform: "translateX(-12px)",
        },
        "100%": {
            opacity: 1,
            transform: "translateX(0)",
        },
    },
    drawerContent: {
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.paper,
    },
    temporaryDrawerContent: {
        animation:
            "$drawerContentEnter 320ms cubic-bezier(0.22, 1, 0.36, 1) both",
        "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
        },
    },
    drawerHeader: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.25),
        minHeight: NAV_HEADER_HEIGHT,
        flexShrink: 0,
        padding: theme.spacing(0, 2),
        color: "#fff",
        backgroundColor: BRAND_GREEN,
        backgroundImage: BRAND_GRADIENT,
        borderBottom: BRAND_HAIRLINE,
        // No right edge on purpose. The paper's own border is off so that each
        // stacked section can own its edge, and the header wants none at all:
        // its green runs flush to x=240, where the app bar's begins, so the
        // band crosses the join with nothing drawn on the seam. The sections
        // below still carry the pale divider.
    },
    drawerBody: {
        flex: 1,
        overflowY: "auto",
        padding: theme.spacing(1.25, 1, 1.5),
        borderRight: `1px solid ${theme.palette.divider}`,
    },
}));

export default function HamburgerMenu({ pinned = false }) {
    const history = useHistory();
    const location = useLocation();

    const classes = useStyles();

    const [isOpen, setIsOpen] = useState(false);

    const { currentUserData } = useContext(UserContext);
    const { conversations, notifications } = useContext(DatabaseContext);

    // The way back to their Nodiant Coder key; the home feed doesn't show its cards
    const hasDesktopLicence = useHasCurrentDesktopLicence(
        currentUserData?.key,
        notifications,
    );

    const [userCanEditUsers, setUserCanEditUsers] = useState(false);
    const [userCanViewCalendar, setUserCanViewCalendar] = useState(false);
    const [userCanSeeManagement, setUserCanSeeManagement] = useState(false);
    const [userCanOpenMessages, setUserCanOpenMessages] = useState(false);

    const unreadMessages = countUnreadMessages(
        conversations,
        currentUserData?.key,
    );
    const userName =
        [currentUserData?.firstName, currentUserData?.lastName]
            .filter(Boolean)
            .join(" ") || "Your account";

    function routeIsActive(...routes) {
        return routes.some((route) =>
            route === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(route),
        );
    }

    useEffect(() => {
        if (currentUserData && currentUserData.permissions) {
            if (currentUserData.permissions.addEditUsers) {
                setUserCanEditUsers(true);
            } else {
                setUserCanEditUsers(false);
            }

            if (currentUserData.permissions.openMessage) {
                setUserCanOpenMessages(true);
            } else {
                setUserCanOpenMessages(false);
            }

            if (currentUserData.permissions.viewCalendar) {
                setUserCanViewCalendar(true);
            } else {
                setUserCanViewCalendar(false);
            }
            if (
                currentUserData.role !== "Player" &&
                currentUserData.role !== "FAW Player" &&
                currentUserData.role !== "Club Player" &&
                currentUserData.role !== "Coach Ed Student" &&
                currentUserData.role !== "FAW Coach Ed Student" &&
                currentUserData.role !== "Club Coach Ed Student" &&
                currentUserData.role !== "FAW Parent" &&
                currentUserData.role !== "Club Parent" &&
                currentUserData.role !== "Referee"
            ) {
                setUserCanSeeManagement(true);
            } else {
                setUserCanSeeManagement(false);
            }
        }
    }, [currentUserData]);

    function handleClickHome() {
        history.replace("/");
    }

    function handleClickUsers() {
        history.replace("/users");
    }

    function handleClickDesktopApp() {
        history.replace("/desktop-access");
    }

    function handleClickHubs() {
        history.replace("/hubs");
    }

    function handleClickFiles() {
        history.replace("/files");
    }

    function handleClickCalendar() {
        history.replace("/calendar");
    }

    function handleClickMessages() {
        history.replace("/messages");
    }

    function handleClickPlaylists() {
        history.replace("/playlists");
    }

    function handleClickSurveys() {
        history.replace("/surveys/s");
    }

    function handleClickProjects() {
        history.replace("/projects/s");
    }

    function handleClickMyDesktopApp() {
        history.push("/desktop-app");
    }

    function handleClickMyAccount() {
        history.push({
            pathname: "/myaccount",
        });
    }

    const toggleDrawer = (open) => (event) => {
        if (
            event.type === "keydown" &&
            (event.key === "Tab" || event.key === "Shift")
        ) {
            return;
        }

        setIsOpen(open);
    };

    const list = ({ permanent = false } = {}) => (
        <div
            role="presentation"
            onClick={toggleDrawer(false)}
            onKeyDown={toggleDrawer(false)}
            className={`${classes.drawerContent} ${
                permanent ? "" : classes.temporaryDrawerContent
            }`}
        >
            <Box className={classes.drawerHeader}>
                <Box
                    component="img"
                    src="/images/logo512_large.png"
                    alt=""
                    aria-hidden="true"
                    sx={{
                        width: 30,
                        height: 30,
                        flexShrink: 0,
                        borderRadius: 2,
                        border: "1px solid rgba(255, 255, 255, 0.27)",
                    }}
                />
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography sx={brandNameSx}>Nodiant</Typography>
                </Box>
                {!permanent && (
                    <IconButton
                        aria-label="Close navigation"
                        onClick={toggleDrawer(false)}
                        sx={{
                            width: 36,
                            height: 36,
                            flexShrink: 0,
                            borderRadius: 2,
                            color: "rgba(255, 255, 255, 0.94)",
                            "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.16)",
                            },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>

            <Box className={classes.drawerBody}>
                <List disablePadding>
                    <ListItemButton
                        selected={routeIsActive("/")}
                        sx={navItemSx}
                        onClick={handleClickHome}
                    >
                        <ListItemIcon>
                            <HomeOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary="Home" />
                    </ListItemButton>
                    <ListItemButton
                        selected={routeIsActive(
                            "/files",
                            "/file/",
                            "/video/",
                            "/audio/",
                            "/image/",
                            "/pdf/",
                            "/link/",
                        )}
                        sx={navItemSx}
                        onClick={handleClickFiles}
                    >
                        <ListItemIcon>
                            <FolderOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary="Files" />
                    </ListItemButton>
                    {userCanOpenMessages && (
                        <ListItemButton
                            selected={routeIsActive("/messages")}
                            sx={navItemSx}
                            onClick={handleClickMessages}
                            aria-label={
                                unreadMessages > 0
                                    ? "Messages, " +
                                      unreadMessages +
                                      " unread"
                                    : "Messages"
                            }
                        >
                            <ListItemIcon>
                                <ChatBubbleOutlineIcon />
                            </ListItemIcon>
                            <ListItemText primary="Messages" />
                            {unreadMessages > 0 && (
                                <Box
                                    aria-hidden="true"
                                    sx={{
                                        minWidth: 20,
                                        px: 0.75,
                                        flexShrink: 0,
                                        borderRadius: 5,
                                        color: "#fff",
                                        backgroundColor: "secondary.main",
                                        fontSize: 11,
                                        fontWeight: 700,
                                        lineHeight: "20px",
                                        textAlign: "center",
                                    }}
                                >
                                    {unreadMessages}
                                </Box>
                            )}
                        </ListItemButton>
                    )}
                    {userCanViewCalendar && (
                        <ListItemButton
                            selected={routeIsActive("/calendar")}
                            sx={navItemSx}
                            onClick={handleClickCalendar}
                        >
                            <ListItemIcon>
                                <TodayOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText primary="Calendar" />
                        </ListItemButton>
                    )}
                    <ListItemButton
                        selected={routeIsActive("/playlists", "/playlist")}
                        sx={navItemSx}
                        onClick={handleClickPlaylists}
                    >
                        <ListItemIcon>
                            <TheatersOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary="Playlists" />
                    </ListItemButton>
                    <ListItemButton
                        selected={routeIsActive("/surveys")}
                        sx={navItemSx}
                        onClick={handleClickSurveys}
                    >
                        <ListItemIcon>
                            <AssignmentOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary="Surveys" />
                    </ListItemButton>
                    <ListItemButton
                        selected={routeIsActive("/projects")}
                        sx={navItemSx}
                        onClick={handleClickProjects}
                    >
                        <ListItemIcon>
                            <ListAltOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primary="Projects" />
                    </ListItemButton>
                </List>

                {userCanSeeManagement && (
                    <>
                        <Divider
                            sx={{
                                mt: 1.5,
                                mx: 1.25,
                                borderColor: "divider",
                            }}
                        />
                        <Typography sx={sectionLabelSx}>
                            Management
                        </Typography>
                        <List disablePadding>
                            {userCanEditUsers && (
                                <ListItemButton
                                    sx={navItemSx}
                                    selected={routeIsActive("/users")}
                                    onClick={handleClickUsers}
                                >
                                    <ListItemIcon>
                                        <PersonOutlineIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Users" />
                                </ListItemButton>
                            )}


                            <ListItemButton
                                sx={navItemSx}
                                selected={routeIsActive("/hubs")}
                                onClick={handleClickHubs}
                            >
                                <ListItemIcon>
                                    <GroupOutlinedIcon />
                                </ListItemIcon>
                                <ListItemText primary="Hubs" />
                            </ListItemButton>
                            {currentUserData?.role === GLOBAL_ROLE && (
                                <ListItemButton
                                    sx={navItemSx}
                                    selected={routeIsActive("/desktop-access")}
                                    onClick={handleClickDesktopApp}
                                >
                                    <ListItemIcon>
                                        <DesktopMacOutlinedIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Desktop App" />
                                </ListItemButton>
                            )}
                        </List>
                    </>
                )}
            </Box>

            {hasDesktopLicence && (
                <ButtonBase
                    onClick={handleClickMyDesktopApp}
                    aria-label="Desktop App"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 1,
                        flexShrink: 0,
                        px: 1.75,
                        py: 0.875,
                        borderTop: "1px solid",
                        borderRight: "1px solid",
                        borderColor: "divider",
                        color: routeIsActive("/desktop-app")
                            ? "primary.main"
                            : "text.secondary",
                        fontSize: 12.5,
                        textAlign: "left",
                        transition: "background-color 120ms ease, color 120ms ease",
                        "&:hover": {
                            backgroundColor: "action.hover",
                            color: "text.primary",
                        },
                        "&:focus-visible": {
                            outline: "2px solid",
                            outlineColor: "primary.main",
                            outlineOffset: -2,
                        },
                    }}
                >
                    <DesktopMacOutlinedIcon sx={{ fontSize: 17 }} />
                    Desktop App
                </ButtonBase>
            )}

            <ButtonBase
                onClick={handleClickMyAccount}
                aria-label="My Account"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    flexShrink: 0,
                    px: 1.75,
                    py: 1.25,
                    borderTop: "1px solid",
                    borderRight: "1px solid",
                    borderColor: "divider",
                    textAlign: "left",
                    transition: "background-color 120ms ease",
                    "&:hover": {
                        backgroundColor: "action.hover",
                    },
                    "& .MuiAvatar-root": {
                        width: 32,
                        height: 32,
                        fontSize: 13,
                    },
                    "&:focus-visible": {
                        outline: "2px solid",
                        outlineColor: "primary.main",
                        outlineOffset: -2,
                    },
                }}
            >
                <UserAvatar userOrHub={currentUserData} path="users" />
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography
                        noWrap
                        sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.3 }}
                    >
                        {userName}
                    </Typography>
                    <Typography
                        noWrap
                        sx={{
                            display: "block",
                            fontSize: 11,
                            color: "text.secondary",
                            lineHeight: 1.3,
                        }}
                    >
                        {currentUserData?.customRoleName ||
                            currentUserData?.role ||
                            "Nodiant user"}
                    </Typography>
                </Box>
                <ChevronRightIcon
                    sx={{ fontSize: 18, flexShrink: 0, color: "text.disabled" }}
                />
            </ButtonBase>
        </div>
    );

    return (
        <>
            <IconButton
                edge="start"
                color="inherit"
                onClick={toggleDrawer(true)}
                aria-label="Open navigation"
                sx={{
                    display: pinned
                        ? { xs: "inline-flex", md: "none" }
                        : "inline-flex",
                    mr: 1,
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    color: "rgba(255, 255, 255, 0.94)",
                    "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.14)",
                    },
                }}
            >
                <MenuIcon />
            </IconButton>
            <Drawer
                anchor="left"
                open={isOpen}
                onClose={toggleDrawer(false)}
                sx={{
                    display: pinned ? { xs: "block", md: "none" } : "block",
                    "& .MuiDrawer-paper": {
                        width: 288,
                        maxWidth: "calc(100vw - 28px)",
                        borderRight: 0,
                        backgroundColor: "background.paper",
                        boxShadow: (theme) =>
                            theme.palette.mode === "dark"
                                ? "12px 0 38px rgba(0, 0, 0, 0.42)"
                                : "12px 0 38px rgba(20, 35, 25, 0.16)",
                    },
                    "& .MuiBackdrop-root": {
                        backgroundColor: "rgba(12, 25, 16, 0.42)",
                        backdropFilter: "blur(2px)",
                    },
                }}
            >
                {list()}
            </Drawer>
            {pinned && (
                // A permanent Drawer renders inline rather than through a
                // portal, and this component sits inside the AppBar. Any
                // filter, transform or backdrop-filter on the bar would make it
                // a containing block for fixed children and anchor the sidebar
                // to the bar instead of the viewport, so keep it on the body.
                <Portal>
                    <Drawer
                        variant="permanent"
                        open
                        sx={{
                            display: { xs: "none", md: "block" },
                            "& .MuiDrawer-paper": {
                                width: DESKTOP_NAV_WIDTH,
                                boxSizing: "border-box",
                                borderRight: 0,
                                backgroundColor: "background.paper",
                                zIndex: (theme) => theme.zIndex.appBar + 1,
                            },
                        }}
                    >
                        {list({ permanent: true })}
                    </Drawer>
                </Portal>
            )}
        </>
    );
}
